import React, { useState } from 'react';
import type { LocalizedString } from '../../types';
import type { AdminSection, SectionInput } from '../../services/adminApi';
import LocalizedInput from './LocalizedInput';
import { EMPTY_LOCALIZED } from './localized';
import styles from './AdminCrud.module.css';

interface SectionFormProps {
  initial?: AdminSection;
  onSubmit: (input: SectionInput) => Promise<void>;
  onCancel: () => void;
}

/** Create/edit form for a section: localized title, optional flag, selection limit */
const SectionForm: React.FC<SectionFormProps> = ({ initial, onSubmit, onCancel }) => {
  const [title, setTitle] = useState<LocalizedString>(initial?.title ?? EMPTY_LOCALIZED);
  const [optional, setOptional] = useState<boolean>(initial?.optional ?? false);
  const [optionsNum, setOptionsNum] = useState<string>(
    initial?.options_num !== undefined && initial?.options_num !== null
      ? String(initial.options_num)
      : ''
  );
  const [sortOrder, setSortOrder] = useState<string>(String(initial?.sort_order ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handle_submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!title.ru.trim()) {
      setError('Укажите название на русском');
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({
        title,
        optional,
        options_num: optionsNum.trim() === '' ? null : Number(optionsNum),
        sort_order: Number(sortOrder) || 0,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to save section');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className={styles.form_panel} onSubmit={handle_submit}>
      <LocalizedInput
        label="Section title"
        field_key="title"
        value={title}
        onChange={setTitle}
      />

      <div className={styles.form_grid} style={{ marginTop: 'var(--spacing-md)' }}>
        <label className={styles.checkbox_row}>
          <input
            type="checkbox"
            checked={optional}
            onChange={(event) => setOptional(event.target.checked)}
          />
          Optional section
        </label>

        <div className={styles.field}>
          <span className={styles.field_label}>Max selections (empty = unlimited)</span>
          <input
            className={styles.input}
            type="number"
            min={1}
            value={optionsNum}
            onChange={(event) => setOptionsNum(event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.field_label}>Sort order</span>
          <input
            className={styles.input}
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
      </div>

      {error && <p className={styles.error_text}>{error}</p>}

      <div className={styles.actions_row}>
        <button type="submit" className={styles.button_primary} disabled={isSaving}>
          {isSaving ? 'Saving…' : initial ? 'Save section' : 'Add section'}
        </button>
        <button type="button" className={styles.button_secondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default SectionForm;
